import React, { useState } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const PaymentTest: React.FC = () => {
  const { user, businessId } = useAuth();
  const [testResult, setTestResult] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const runBasicTest = async () => {
    setLoading(true);
    setTestResult('Running basic database test...\n');

    try {
      // Test 1: Check if user is authenticated
      if (!user) {
        setTestResult(prev => prev + '❌ FAIL: User not authenticated\n');
        return;
      }
      setTestResult(prev => prev + '✅ PASS: User authenticated\n');

      // Test 2: Check if business ID exists
      if (!businessId) {
        setTestResult(prev => prev + '❌ FAIL: No business ID\n');
        return;
      }
      setTestResult(prev => prev + `✅ PASS: Business ID: ${businessId}\n`);

      // Test 3: Check if payments table exists
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('id')
        .limit(1);

      if (paymentsError) {
        setTestResult(prev => prev + `❌ FAIL: Payments table error: ${paymentsError.message}\n`);
        return;
      }
      setTestResult(prev => prev + '✅ PASS: Payments table exists\n');

      // Test 4: Check if accounts table exists and has data
      const { data: accounts, error: accountsError } = await supabase
        .from('accounts')
        .select('id, name, type, balance')
        .eq('business_id', businessId)
        .limit(5);

      if (accountsError) {
        setTestResult(prev => prev + `❌ FAIL: Accounts table error: ${accountsError.message}\n`);
        return;
      }

      if (!accounts || accounts.length === 0) {
        setTestResult(prev => prev + '❌ FAIL: No accounts found for this business\n');
        return;
      }

      setTestResult(prev => prev + `✅ PASS: Found ${accounts.length} accounts\n`);
      accounts.forEach(account => {
        setTestResult(prev => prev + `   - ${account.name} (${account.type}): ${account.balance}\n`);
      });

      // Test 5: Try to create a test payment
      const testAccount = accounts[0];
      const testPayment = {
        business_id: businessId,
        account_id: testAccount.id,
        amount: 1000,
        payment_date: new Date().toISOString().split('T')[0],
        description: 'Test payment from debug tool'
      };

      setTestResult(prev => prev + '\n🔄 Testing payment creation...\n');

      const { data: paymentResult, error: paymentError } = await supabase
        .from('payments')
        .insert([testPayment])
        .select();

      if (paymentError) {
        setTestResult(prev => prev + `❌ FAIL: Payment creation error: ${paymentError.message}\n`);
        setTestResult(prev => prev + `   Code: ${paymentError.code}\n`);
        setTestResult(prev => prev + `   Details: ${paymentError.details}\n`);
        setTestResult(prev => prev + `   Hint: ${paymentError.hint}\n`);
        return;
      }

      if (paymentResult && paymentResult[0]) {
        setTestResult(prev => prev + `✅ PASS: Payment created successfully!\n`);
        setTestResult(prev => prev + `   Payment ID: ${paymentResult[0].id}\n`);

        // Clean up test payment
        await supabase.from('payments').delete().eq('id', paymentResult[0].id);
        setTestResult(prev => prev + '🧹 Test payment cleaned up\n');
      }

      setTestResult(prev => prev + '\n🎉 All tests passed! Payment functionality should work.\n');

    } catch (err) {
      setTestResult(prev => prev + `❌ ERROR: ${err instanceof Error ? err.message : 'Unknown error'}\n`);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResult('');
  };

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-2xl font-bold mb-4">Payment Debug Test</h2>

      <div className="mb-4 flex gap-2">
        <Button
          onClick={runBasicTest}
          disabled={loading}
          variant="primary"
        >
          {loading ? 'Running Tests...' : 'Run Payment Tests'}
        </Button>

        <Button
          onClick={clearResults}
          variant="outline"
        >
          Clear Results
        </Button>
      </div>

      <div className="bg-gray-100 p-4 rounded-lg font-mono text-sm">
        {testResult || 'Click "Run Payment Tests" to start debugging...'}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded">
        <h3 className="font-bold mb-2">What this test checks:</h3>
        <ul className="list-disc list-inside text-sm space-y-1">
          <li>User authentication status</li>
          <li>Business ID availability</li>
          <li>Payments table existence</li>
          <li>Accounts table and data availability</li>
          <li>Payment creation permissions (RLS)</li>
          <li>Foreign key constraints</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentTest;